// frontend/src/components/FinanceReport/Accounting/our_allocations/DistributionTable.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const money = (v, c) => {
  const n = Number(v || 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const s = c?.symbol || "";
  return s ? `${s} ${n}` : n;
};

const KIND_META = {
  our: {
    label: "Наша оплата поставщику",
    badge: "bg-blue-100 text-blue-700",
  },
  profit: {
    label: "В прибыль компании",
    badge: "bg-violet-100 text-violet-700",
  },
  supplier: {
    label: "Предоплата поставщику",
    badge: "bg-emerald-100 text-emerald-700",
  },
};

const STATUS_LABELS = { PENDING: "Ожидает", PAID: "Оплачен" };

function DistributionRow({
  kind,
  item,
  isEditing,
  editDraft,
  setEditDraft,
  editError,
  editSaving,
  currenciesById,
  companyAccounts,
  suppliers,
  orders,
  activeOrders,
  currencies,
  ordersById,
  accountsById,
  suppliersById,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onOpenDocs,
}) {
  const navigate = useNavigate();
  const meta = KIND_META[kind];
  const currency = currenciesById[item.currency] || null;

  const orderId =
    item.order && typeof item.order === "object" ? item.order.id : item.order;
  const orderObj = orderId ? ordersById[orderId] : null;
  const orderCode =
    item.order_code || orderObj?.order_code || (orderId ? `#${orderId}` : "—");

  const accountId = item.account;
  const accountObj = accountId ? accountsById[accountId] : null;
  const accountName =
    item.account_name ||
    accountObj?.name ||
    (accountId ? `Счёт #${accountId}` : "—");

  const supplierId = item.supplier;
  const supplierObj = supplierId ? suppliersById[supplierId] : null;
  const supplierName =
    item.supplier_name ||
    supplierObj?.name ||
    (supplierId ? `Поставщик #${supplierId}` : "—");

  const renderOrderCell = () => {
    if (isEditing && kind === "our") {
      // Только активные + текущий (если он завершён — чтобы не сломать существующую строку)
      const list = [...activeOrders];
      if (
        editDraft.order &&
        !list.some((o) => String(o.id) === String(editDraft.order))
      ) {
        const cur = ordersById[editDraft.order] || {
          id: editDraft.order,
          order_code: `#${editDraft.order}`,
          status: "completed",
        };
        list.unshift(cur);
      }
      return (
        <select
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={editDraft.order || ""}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, order: e.target.value }))
          }
        >
          <option value="">—</option>
          {list.map((o) => (
            <option key={o.id} value={o.id}>
              {o.order_code || `#${o.id}`}
              {o.status === "completed" ? " (завершён)" : ""}
              {o.client_data || o.client_name
                ? ` — ${o.client_data || o.client_name}`
                : ""}
            </option>
          ))}
        </select>
      );
    }
    if (orderId) {
      return (
        <button
          type="button"
          onClick={() => navigate(`/orders/${orderId}`)}
          className="text-left text-xs text-blue-700 hover:underline"
        >
          {orderCode}
        </button>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  const renderAccountCell = () => {
    if (isEditing && kind === "profit") {
      return (
        <select
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={editDraft.account || ""}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, account: e.target.value }))
          }
        >
          <option value="">—</option>
          {companyAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.currency ? ` (${a.currency})` : ""}
            </option>
          ))}
        </select>
      );
    }
    if (isEditing && kind === "supplier") {
      return (
        <select
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={editDraft.supplier || ""}
          onChange={(e) =>
            setEditDraft((d) => ({ ...d, supplier: e.target.value }))
          }
        >
          <option value="">—</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      );
    }
    if (kind === "our" && orderId) {
      return (
        <button
          type="button"
          onClick={() => navigate(`/orders/${orderId}/finances`)}
          className="text-left text-xs text-blue-700 hover:underline"
        >
          Счёт заказа {orderCode}
        </button>
      );
    }
    if (kind === "profit" && accountId) {
      return (
        <button
          type="button"
          onClick={() => navigate(`/accounting/company-accounts/${accountId}`)}
          className="text-left text-xs text-blue-700 hover:underline"
        >
          {accountName}
        </button>
      );
    }
    if (kind === "supplier" && supplierId) {
      return (
        <button
          type="button"
          onClick={() =>
            navigate(`/accounting/supplier-accounts/${supplierId}`)
          }
          className="text-left text-xs text-blue-700 hover:underline"
        >
          {supplierName}
        </button>
      );
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  const handleDeleteClick = () => {
    if (
      window.confirm(`Удалить «${meta.label}» #${item.id}?`)
    ) {
      onDelete(kind, item);
    }
  };

  return (
    <tr className={isEditing ? "bg-yellow-50" : "bg-white"}>
      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            type="date"
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.date || ""}
            onChange={(e) =>
              setEditDraft((d) => ({ ...d, date: e.target.value }))
            }
          />
        ) : item.date ? (
          new Date(`${item.date}T00:00:00`).toLocaleDateString("ru-RU")
        ) : (
          "—"
        )}
      </td>

      <td className="border p-2 align-top">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}
        >
          {meta.label}
        </span>
      </td>

      <td className="border p-2 align-top">{renderOrderCell()}</td>
      <td className="border p-2 align-top">{renderAccountCell()}</td>

      <td className="border p-2 align-top font-semibold text-slate-900">
        {isEditing ? (
          <div className="flex gap-1">
            <input
              type="number"
              onWheel={(e) => e.currentTarget.blur()}
              min="0.01"
              step="0.01"
              className="w-full rounded border px-1 py-0.5 text-xs"
              value={editDraft.amount || ""}
              onChange={(e) =>
                setEditDraft((d) => ({ ...d, amount: e.target.value }))
              }
            />
            <select
              className="w-16 rounded border px-1 py-0.5 text-xs"
              value={editDraft.currency || ""}
              onChange={(e) =>
                setEditDraft((d) => ({ ...d, currency: e.target.value }))
              }
            >
              <option value="">—</option>
              {currencies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.symbol}
                </option>
              ))}
            </select>
          </div>
        ) : (
          money(item.amount, currency)
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.payment_status || "PENDING"}
            onChange={(e) =>
              setEditDraft((d) => ({ ...d, payment_status: e.target.value }))
            }
          >
            <option value="PENDING">Ожидает</option>
            <option value="PAID">Оплачен</option>
          </select>
        ) : (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              item.payment_status === "PAID"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {STATUS_LABELS[item.payment_status] || item.payment_status || "—"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.comment || ""}
            onChange={(e) =>
              setEditDraft((d) => ({ ...d, comment: e.target.value }))
            }
          />
        ) : (
          <span
            className="block max-w-[14rem] truncate text-xs text-slate-600"
            title={item.comment || ""}
          >
            {item.comment || "—"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.payment_agent || ""}
            onChange={(e) =>
              setEditDraft((d) => ({ ...d, payment_agent: e.target.value }))
            }
          />
        ) : (
          <span className="text-xs text-slate-600">
            {item.payment_agent || "—"}
          </span>
        )}
      </td>

      <td className="border p-2 align-top">
      <button
        type="button"
        onClick={() => onOpenDocs(kind, item)}
        className={`text-xs underline ${
          (item.documents?.length || 0) > 0
            ? "text-blue-700 hover:text-blue-900"
            : "text-amber-600 hover:text-amber-800"
        }`}
        title="Открыть документы / загрузить файлы"
      >
        📎 Документы ({item.documents?.length || 0})
      </button>
      </td>

      <td className="border p-2 align-top">
        {isEditing ? (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              disabled={editSaving}
              onClick={onSaveEdit}
              className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {editSaving ? "…" : "Сохранить"}
            </button>
            <button
              type="button"
              disabled={editSaving}
              onClick={onCancelEdit}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Отмена
            </button>
            {editError && (
              <div className="mt-1 w-full text-xs text-red-600">{editError}</div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => onStartEdit(kind, item)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs"
              title="Редактировать"
            >
              ✏
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              className="rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-600"
              title="Удалить"
            >
              🗑
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function AddRow({
  addType,
  setAddType,
  addDraft,
  setAddDraft,
  addSaving,
  currencies,
  companyAccounts,
  suppliers,
  orders,
  onAdd,
}) {
  return (
    <tr className="bg-white">
      <td className="border p-1">
        <input
          type="date"
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={addDraft.date || ""}
          onChange={(e) =>
            setAddDraft((d) => ({ ...d, date: e.target.value }))
          }
        />
      </td>
      <td className="border p-1">
        <select
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={addType}
          onChange={(e) => setAddType(e.target.value)}
        >
          <option value="our">Наша оплата поставщику</option>
          <option value="profit">В прибыль компании</option>
          <option value="supplier">Предоплата поставщику</option>
        </select>
      </td>
      <td className="border p-1">
        {addType === "our" ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={addDraft.order || ""}
            onChange={(e) =>
              setAddDraft((d) => ({ ...d, order: e.target.value }))
            }
          >
            <option value="">—</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.order_code || `#${o.id}`}
                {o.client_data || o.client_name
                  ? ` — ${o.client_data || o.client_name}`
                  : ""}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="border p-1">
        {addType === "profit" ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={addDraft.account || ""}
            onChange={(e) =>
              setAddDraft((d) => ({ ...d, account: e.target.value }))
            }
          >
            <option value="">—</option>
            {companyAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {a.currency ? ` (${a.currency})` : ""}
              </option>
            ))}
          </select>
        ) : addType === "supplier" ? (
          <select
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={addDraft.supplier || ""}
            onChange={(e) =>
              setAddDraft((d) => ({ ...d, supplier: e.target.value }))
            }
          >
            <option value="">—</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="border p-1">
        <div className="flex gap-1">
          <input
            type="number"
            onWheel={(e) => e.currentTarget.blur()}
            min="0.01"
            step="0.01"
            className="w-full rounded border px-1 py-0.5 text-xs"
            placeholder="Сумма"
            value={addDraft.amount || ""}
            onChange={(e) =>
              setAddDraft((d) => ({ ...d, amount: e.target.value }))
            }
          />
          <select
            className="w-16 rounded border px-1 py-0.5 text-xs"
            value={addDraft.currency || ""}
            onChange={(e) =>
              setAddDraft((d) => ({ ...d, currency: e.target.value }))
            }
          >
            <option value="">—</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.symbol}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td className="border p-1">
        <select
          className="w-full rounded border px-1 py-0.5 text-xs"
          value={addDraft.payment_status || "PENDING"}
          onChange={(e) =>
            setAddDraft((d) => ({ ...d, payment_status: e.target.value }))
          }
        >
          <option value="PENDING">Ожидает</option>
          <option value="PAID">Оплачен</option>
        </select>
      </td>
      <td className="border p-1">
        <input
          className="w-full rounded border px-1 py-0.5 text-xs"
          placeholder="Коммент."
          value={addDraft.comment || ""}
          onChange={(e) =>
            setAddDraft((d) => ({ ...d, comment: e.target.value }))
          }
        />
      </td>
      <td className="border p-1">
        <input
          className="w-full rounded border px-1 py-0.5 text-xs"
          placeholder="Через"
          value={addDraft.payment_agent || ""}
          onChange={(e) =>
            setAddDraft((d) => ({ ...d, payment_agent: e.target.value }))
          }
        />
      </td>
      <td className="border p-1 text-xs text-slate-400">—</td>
      <td className="border p-1">
        <button
          type="button"
          onClick={onAdd}
          disabled={addSaving}
          className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {addSaving ? "..." : "Добавить"}
        </button>
      </td>
    </tr>
  );
}

export default function DistributionTable({
  rows,
  currenciesById,
  companyAccounts,
  suppliers,
  orders,
  currencies,
  editing,
  editDraft,
  setEditDraft,
  editError,
  editSaving,
  addType,
  setAddType,
  addDraft,
  setAddDraft,
  addSaving,
  addError,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onOpenDocs,
  onAdd,
}) {
  const ordersById = useMemo(() => {
    const m = {};
    orders.forEach((o) => {
      m[o.id] = o;
    });
    return m;
  }, [orders]);

  // Только незавершённые заказы для выпадающего списка
  const activeOrders = useMemo(
    () => orders.filter((o) => o.status !== "completed"),
    [orders]
  );

  const accountsById = useMemo(() => {
    const m = {};
    companyAccounts.forEach((a) => {
      m[a.id] = a;
    });
    return m;
  }, [companyAccounts]);

  const suppliersById = useMemo(() => {
    const m = {};
    suppliers.forEach((s) => {
      m[s.id] = s;
    });
    return m;
  }, [suppliers]);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <th className="border p-2">Дата</th>
              <th className="border p-2">Тип</th>
              <th className="border p-2">Заказ</th>
              <th className="border p-2">Счёт</th>
              <th className="border p-2">Сумма</th>
              <th className="border p-2">Статус</th>
              <th className="border p-2">Комментарий</th>
              <th className="border p-2">Платёж через</th>
              <th className="border p-2">Документы</th>
              <th className="border p-2">Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={10}
                  className="border p-8 text-center text-slate-500"
                >
                  Распределений пока нет. Добавьте первое ниже.
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const isEditing =
                editing &&
                editing.kind === row.kind &&
                String(editing.id) === String(row.item.id);
              return (
                <DistributionRow
                  key={`${row.kind}-${row.item.id}`}
                  kind={row.kind}
                  item={row.item}
                  isEditing={isEditing}
                  editDraft={editDraft}
                  setEditDraft={setEditDraft}
                  editError={editError}
                  editSaving={editSaving}
                  currenciesById={currenciesById}
                  companyAccounts={companyAccounts}
                  suppliers={suppliers}
                  orders={orders}
                  activeOrders={activeOrders}
                  currencies={currencies}
                  ordersById={ordersById}
                  accountsById={accountsById}
                  suppliersById={suppliersById}
                  onStartEdit={onStartEdit}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                  onDelete={onDelete}
                  onOpenDocs={onOpenDocs}
                />
              );
            })}

            <AddRow
              addType={addType}
              setAddType={setAddType}
              addDraft={addDraft}
              setAddDraft={setAddDraft}
              addSaving={addSaving}
              currencies={currencies}
              companyAccounts={companyAccounts}
              suppliers={suppliers}
              orders={activeOrders}
              onAdd={onAdd}
            />
          </tbody>
        </table>
      </div>

      {addError && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
          {addError}
        </div>
      )}
    </div>
  );
}